import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Seo } from "../components/Seo";
import { Footer } from "../components/Footer";
import { Screenshot } from "../components/Screenshot";
import { FEATURES_PAGE } from "../data/homepage";
import { ArrowRight, MessageSquareText, Compass } from "lucide-react";

const FeatureRow = ({ s, i }) => (
  <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center" data-testid={`feature-section-${s.shot}`}>
    <div className={`h-64 sm:h-80 ${i % 2 ? "lg:order-2" : ""}`}><Screenshot id={s.shot} /></div>
    <div>
      <span className="text-xs uppercase tracking-widest text-[#C8553D]">0{i + 1}</span>
      <h2 className="font-display text-3xl sm:text-4xl mt-2 mb-4 leading-tight">{s.heading}</h2>
      <p className="text-foreground/90 leading-relaxed text-lg">{s.body}</p>
    </div>
  </section>
);

const Comparison = ({ c }) => (
  <section className="mt-24" data-testid="features-comparison">
    <h2 className="font-display text-3xl sm:text-4xl mb-3">{c.heading}</h2>
    <p className="text-muted-foreground mb-8 max-w-2xl">{c.intro}</p>
    <div className="rounded-3xl border border-black/5 bg-white/60 overflow-hidden">
      <div className="grid grid-cols-2 border-b border-black/5">
        <div className="p-4 sm:p-5 flex items-center gap-2 text-sm font-medium text-muted-foreground"><MessageSquareText className="w-4 h-4" /> {c.cols[0]}</div>
        <div className="p-4 sm:p-5 flex items-center gap-2 text-sm font-medium text-[#1A3B2B] bg-[#F3EFE8]/70"><Compass className="w-4 h-4 text-[#C8553D]" /> {c.cols[1]}</div>
      </div>
      {c.rows.map(([a, b], i) => (
        <div key={i} className="grid grid-cols-2 border-b border-black/5 last:border-0" data-testid={`comparison-row-${i}`}>
          <div className="p-4 sm:p-5 text-sm text-muted-foreground leading-relaxed">{a}</div>
          <div className="p-4 sm:p-5 text-sm leading-relaxed bg-[#F3EFE8]/70">{b}</div>
        </div>
      ))}
    </div>
  </section>
);

export default function Features() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const p = FEATURES_PAGE;
  return (
    <div className="min-h-screen grain relative" data-testid="features-page">
      <Seo title={p.title} description={p.description} path={p.path} type="article" jsonLd={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: p.comparison.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }} />
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <span className="inline-flex text-xs uppercase tracking-widest text-[#C8553D] mb-4">How it works</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4 max-w-3xl">{p.h1}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mb-16">{p.intro}</p>
        <div className="space-y-20">
          {p.sections.map((s, i) => <FeatureRow key={s.shot} s={s} i={i} />)}
        </div>
        <Comparison c={p.comparison} />
        <div className="mt-20 rounded-3xl bg-[#1A3B2B] text-[#FDFBF7] p-8 sm:p-10 text-center" data-testid="features-cta-block">
          <h2 className="font-display text-3xl mb-3">{p.ctaHeading}</h2>
          <p className="opacity-85 mb-6 max-w-xl mx-auto">{p.ctaBody}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={() => navigate(user ? "/plan" : "/auth")} data-testid="features-cta" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#C8553D] text-[#FDFBF7] hover:bg-[#a8412f] transition-colors">
              Plan my trip <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/plan-with-us" data-testid="features-plan-with-us" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#FDFBF7]/30 hover:bg-white/10 transition-colors">
              Plan with a human
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
